import { Button, Divider } from "@mui/material";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import RemoveCircleIcon from "@mui/icons-material/RemoveCircle";
import { useFieldArray, useForm, useFormContext } from "react-hook-form";
import Field from "@/core/components/Field";
import { useEffect, useState } from "react";
import { Box } from "@mui/system";
import { useTranslations as t } from "@/core/hooks/useTranslations/useTranslations";
import { AirportData, Field as IField } from "@/core/types/types";
import { generateUUID } from "@datadog/browser-core";

const MAX_STOPOVERS_AMOUNT = 3;

interface Stopover {
  id: string;
  stopoverAirport?: AirportData;
  alternativeTransport?: string;
}

const Stopover = ({ groupItems = [] }: { groupItems: IField[] }) => {
  const [field, ...groupFields] = groupItems;
  const { watch, setValue, clearErrors } = useFormContext();
  const { control } = useForm();
  const newStopover: Stopover = {
    id: generateUUID(),
    stopoverAirport: undefined,
    alternativeTransport: undefined,
  };
  const { fields, append, remove } = useFieldArray({
    name: field.key,
    control,
  });
  const values: Stopover[] = watch(field.key);
  const stopoverAmount: number =
    field?.validate?.maxLength || MAX_STOPOVERS_AMOUNT;
  const [isRendered, setIsRendered] = useState<boolean>(false);

  // Open stopover if the validate required is specified
  if (!isRendered && !fields.length && field.validate?.required) {
    setIsRendered(true);
    append(newStopover);
  }

  useEffect(() => {
    if (fields.length) {
      const fieldData: any[] = fields.map(
        (fieldItem: Stopover, index: number) => {
          const { id } = fieldItem || {};
          const fieldItemIsPrefilled =
            fieldItem.stopoverAirport && fieldItem.alternativeTransport;
          const { stopoverAirport, alternativeTransport } = fieldItemIsPrefilled
            ? fieldItem
            : values?.[index] ?? {};

          return { id, stopoverAirport, alternativeTransport };
        }
      );

      setValue(field.key, fieldData);
    } else {
      clearErrors(field.key);
    }
  }, [fields]);

  useEffect(() => {
    setValue(field.key, []);
  }, []);

  const handleRemove = (index: number): void => {
    if (index === 0) {
      setValue(field.key, []);
    }

    remove(index);
    clearErrors(field.key);
  };

  const handleAdd = (): void => {
    append(newStopover);
  };

  return (
    <>
      <Box id={field.id} mt={2}>
        <Divider></Divider>
      </Box>

      <ul>
        {fields.map((item: { id: string } & Stopover, idx: number) => {
          const fieldName: string = `${field.key}[${idx}]`;
          return (
            <li key={item.id || fieldName}>
              <Box my={2}>
                <Button
                  onClick={() => handleRemove(+idx)}
                  component="label"
                  color="inherit"
                >
                  <RemoveCircleIcon fontSize="medium" />
                  <Box ml={1} sx={{ fontWeight: 700 }}>
                    {idx + 1}. {t(field.properties.removeBtnText)}
                  </Box>
                </Button>
              </Box>
              {groupFields.map((groupField, index) => {
                const required: boolean | undefined =
                  groupField.properties?.required;

                return (
                  <Box my={2} key={index}>
                    <Field
                      field={{
                        ...groupField,
                        key: `${fieldName}.${groupField.key}`,
                        validate: { required },
                        properties: {
                          ...groupField.properties,
                          errorTranslationKey: groupField.key,
                        },
                      }}
                    />
                  </Box>
                );
              })}
            </li>
          );
        })}

        {fields.length !== stopoverAmount && (
          <Box mt={2}>
            <Button
              onClick={() => handleAdd()}
              component="label"
              color="inherit"
              data-cy="add-stopover"
            >
              <AddCircleIcon fontSize="medium" />
              <Box ml={1} sx={{ fontWeight: 700 }}>
                {fields.length ? `${fields.length + 1}. ` : ""}
                {t(field.properties.addBtnText)}
              </Box>
            </Button>
          </Box>
        )}
      </ul>

      <Box my={2}>
        <Divider></Divider>
      </Box>
    </>
  );
};

export default Stopover;
